/* DS&Durga custom scripts */

/*
* Proxy click for the sticky add to cart and default selectors
* Also improves keyboard nav related to focusability and ordering
*/

let pinScrollOnNextVariantChange = false;
let refocusElementId = null;

document.addEventListener('click', (event) => {
  const proxyButton = event.target.closest('[data-option-proxy]');

  if (!proxyButton || proxyButton.classList.contains('is-disabled')) {
    return;
  }

  const optionInput = document.getElementById(proxyButton.dataset.optionProxy);

  if (optionInput && !optionInput.checked) {
    pinScrollOnNextVariantChange = true;
    refocusElementId = proxyButton.dataset.refocusId || proxyButton.id || null;
    optionInput.click();
    optionInput.blur();
  }
});

document.addEventListener('variant:change', () => {
  if (!pinScrollOnNextVariantChange) {
    return;
  }

  pinScrollOnNextVariantChange = false;

  if (refocusElementId) {
    document.getElementById(refocusElementId)?.focus({ preventScroll: true });
    refocusElementId = null;
  }

  const scrollLeft = window.scrollX;
  const scrollTop = window.scrollY;

  requestAnimationFrame(() => window.scrollTo({ left: scrollLeft, top: scrollTop, behavior: 'instant' }));
});

/*
* Buy box upsell: adds the upsell product (plus the currently selected main variant unless upsell-only) in one cart call
*/

const formatMoney = (cents, format) => {
  const [units, decimals] = (cents / 100).toFixed(2).split('.');
  const unitsWithSeparator = (separator) => units.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return format.replace(/{{\s*(\w+)\s*}}/, (match, placeholder) => {
    switch (placeholder) {
      case 'amount': return `${unitsWithSeparator(',')}.${decimals}`;
      case 'amount_no_decimals': return unitsWithSeparator(',');
      case 'amount_with_comma_separator': return `${unitsWithSeparator('.')},${decimals}`;
      case 'amount_no_decimals_with_comma_separator': return unitsWithSeparator('.');
      case 'amount_with_apostrophe_separator': return `${unitsWithSeparator("'")}.${decimals}`;
      default: return match;
    }
  });
};

class UpsellAddButton extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.onClick.bind(this), { signal });
    this.form?.addEventListener('variant:change', this.onVariantChange.bind(this), { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  get form() {
    return document.getElementById(this.getAttribute('form'));
  }

  get upsellOnly() {
    return this.hasAttribute('upsell-only');
  }

  onVariantChange(event) {
    // Upsell-only buttons never include the main variant, so its price/availability don't matter
    if (this.upsellOnly) {
      return;
    }

    const variant = event.detail.variant;

    if (this.button) {
      this.button.disabled = !variant || !variant.available;
    }

    const price = this.closest('.product-upsell__card')?.querySelector('.product-upsell__price');

    if (!variant || !price) {
      return;
    }

    const combinedPrice = variant.price + parseInt(this.getAttribute('variant-price'), 10);
    price.textContent = this.getAttribute('label-template').replace('[price]', formatMoney(combinedPrice, window.themeVariables.settings.moneyFormat));
  }

  async onClick() {
    const form = this.form;
    const mainVariantId = parseInt(form?.elements.id?.value, 10);

    if ((!this.upsellOnly && !mainVariantId) || this.button.getAttribute('aria-busy') === 'true') {
      return;
    }

    const items = [{ id: parseInt(this.getAttribute('variant-id'), 10), quantity: 1 }];

    if (!this.upsellOnly) {
      items.unshift({ id: mainVariantId, quantity: parseInt(form.elements.quantity?.value, 10) || 1 });
    }

    this.button.setAttribute('aria-busy', 'true');

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections: [...sectionsToBundle].join(',')
      })
    });

    const responseJson = await response.json();
    this.button.removeAttribute('aria-busy');

    if (!response.ok) {
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: { message: responseJson.message, tone: 'error' }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }

    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent.sections = responseJson.sections;

    form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: {
        items: responseJson.items,
        cart: cartContent
      }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        cart: cartContent
      }
    }));
  }
}

if (!window.customElements.get('upsell-add-button')) {
  window.customElements.define('upsell-add-button', UpsellAddButton);
}

/*
* Back in stock: submits the selected sold-out variant to Klaviyo's client back-in-stock
* endpoint. Klaviyo owns the subscription, the flow, and the notification sends.
* The buy box re-renders server-side on variant change, so the element always carries the
* current variant id and re-initializes on each swap.
*/

class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.field = this.querySelector('.back-in-stock__field');
    this.input = this.field?.querySelector('input');
    this.submitButton = this.querySelector('.back-in-stock__submit');
    this.message = this.querySelector('.back-in-stock__message');

    this.submitButton?.addEventListener('click', this.onSubmit.bind(this), { signal });
    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onSubmit();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  get companyId() {
    return this.getAttribute('company-id');
  }

  get endpoint() {
    return this.dataset.endpoint || 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  }

  get consentEndpoint() {
    return this.dataset.consentEndpoint || 'https://a.klaviyo.com/client/subscriptions/';
  }

  get smsListId() {
    return this.dataset.smsListId || '';
  }

  get revision() {
    return this.dataset.revision || '2026-04-15';
  }

  // Klaviyo's Shopify catalog stores variants as $shopify:::$default:::<variantId>
  get catalogVariantId() {
    return `$shopify:::$default:::${this.getAttribute('variant-id')}`;
  }

  showMessage(text, isError) {
    if (!this.message) {
      return;
    }

    this.message.textContent = text || '';
    this.message.classList.toggle('text-error', !!isError);
    this.message.classList.toggle('text-success', !isError);
    this.message.hidden = false;
  }

  // Build the Klaviyo channel + profile from an email or phone number.
  // Returns null when the value is neither a valid email nor a usable phone number.
  resolveContact(value) {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return { channels: ['EMAIL'], attributes: { email: value } };
    }

    const digits = value.replace(/\D/g, '');

    if (digits.length >= 10) {
      const phone_number = digits.length === 10 ? `+1${digits}` : `+${digits}`;
      return { channels: ['SMS'], attributes: { phone_number } };
    }

    return null;
  }

  async onSubmit() {
    const value = this.input?.value.trim();
    const contact = value ? this.resolveContact(value) : null;

    if (!contact) {
      this.showMessage(this.dataset.invalidMessage, true);
      this.input?.focus();
      return;
    }

    if (this.submitButton?.getAttribute('aria-busy') === 'true') {
      return;
    }

    this.submitButton?.setAttribute('aria-busy', 'true');

    try {
      // SMS requires explicit marketing consent before Klaviyo will send the notification
      if (contact.channels.includes('SMS')) {
        await this.subscribeSmsConsent(contact.attributes.phone_number);
      }

      await this.subscribeBackInStock(contact);

      this.showMessage(this.dataset.successMessage, false);
      this.field?.setAttribute('hidden', '');
      this.submitButton?.setAttribute('hidden', '');
    } catch (error) {
      console.error('[back-in-stock]', error);
      this.showMessage(this.dataset.errorMessage, true);
    } finally {
      this.submitButton?.removeAttribute('aria-busy');
    }
  }

  async subscribeBackInStock(contact) {
    const response = await fetch(`${this.endpoint}?company_id=${this.companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: this.revision
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: contact.channels,
            profile: {
              data: {
                type: 'profile',
                attributes: contact.attributes
              }
            }
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: this.catalogVariantId }
            }
          }
        }
      })
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(`Klaviyo back in stock ${response.status}: ${detail}`);
    }
  }

  async subscribeSmsConsent(phoneNumber) {
    if (!this.smsListId) {
      throw new Error('Klaviyo SMS list ID is not configured; phone notifications need one.');
    }

    const response = await fetch(`${this.consentEndpoint}?company_id=${this.companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: this.revision
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  phone_number: phoneNumber,
                  subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: this.smsListId } }
          }
        }
      })
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(`Klaviyo SMS consent ${response.status}: ${detail}`);
    }
  }
}

if (!window.customElements.get('back-in-stock-form')) {
  window.customElements.define('back-in-stock-form', BackInStockForm);
}

/*
 * Monk Free Gift picker (cart drawer). Monk injects its manual free-gift widget into the anchor
 * div in the drawer footer; we adapt it to the design — mirror its title/"(N/M)" count into our
 * summary, show the band only while a widget is active, turn an added card into a removable −,
 * show a dot loader during add/remove, and re-render the section after a cart change so the widget
 * re-mounts in place. Only Monk's stable mr- classes are targeted; its hashed sc-* classes change
 * on app updates.
 */
let monkRendering = false;
let monkPickerSeen = false;

// Re-fetch the cart-drawer section so Monk re-mounts its widget in place (otherwise it goes inert
// after an in-drawer add). Only the <cart-drawer> contents are swapped, so listeners/observer survive.
const monkRefreshDrawer = async () => {
  if (monkRendering) {
    return;
  }
  monkRendering = true;

  try {
    const drawer = document.getElementById('cart-drawer');
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
    const data = await response.json();
    const fresh = new DOMParser()
      .parseFromString(data['cart-drawer'], 'text/html')
      .querySelector('cart-drawer');

    if (drawer && fresh) {
      drawer.innerHTML = fresh.innerHTML;
    }
  } catch (error) {
    console.error('Monk GWP: cart-drawer re-render failed', error);
  } finally {
    monkRendering = false;
  }
};

/*
* Mirror Monk's own header (configured title + "(N/M selected)" count) into the accordion
* summary, since we hide Monk's header. The title is whatever the merchant set in Monk.
* Each write is guarded: this markup sits inside the observed subtree, so an unconditional
* write re-fires the observer and loops.
*/
const monkSyncPicker = (anchor) => {
  const picker = anchor.closest('.cart-drawer__gwp');

  if (!picker) {
    return;
  }

  const widget = anchor.querySelector('.mr-embedded-widget');
  const header = widget && widget.querySelector(':scope > div:not(.mr-embedded-widget__products-container)');
  const headerText = header ? header.textContent.trim() : '';

  const labelEl = picker.querySelector('[data-gwp-label]');
  if (labelEl) {
    const label = headerText.split('(')[0].trim();
    if (label && labelEl.textContent !== label) {
      labelEl.textContent = label;
    }
  }

  const countEl = picker.querySelector('[data-gwp-count]');
  if (countEl) {
    const match = headerText.match(/(\d+)\s*\/\s*(\d+)/);
    const next = match ? `(${match[1]}/${match[2]})` : '';
    if (countEl.textContent !== next) {
      countEl.textContent = next;
    }
  }
};

const monkCartDrawer = document.getElementById('cart-drawer');

if (monkCartDrawer && monkCartDrawer.querySelector('.cart-drawer__gwp')) {
  let monkUpdateQueued = false;

  const monkLoadingTitles = new Set();

  const monkCardTitle = (card) => {
    const el = card && card.querySelector('.mr-product-details__product-title');
    return el ? el.textContent.trim() : '';
  };

  // Monk renders each offer's widget into its own anchor div. We drive whichever is active
  const monkAnchors = () => [
    ...monkCartDrawer.querySelectorAll('.cart-drawer__gwp-carousel [id^="mr-div-embedded-cp-"]')
  ];
  const monkActiveAnchor = () => monkAnchors().find((el) => el.querySelector('.mr-product-item')) || null;
  const monkActiveList = () => {
    const anchor = monkActiveAnchor();
    return anchor ? anchor.querySelector('.react-multi-carousel-list') : null;
  };
  
  const monkRemoveGift = (card) => {
    const title = monkCardTitle(card);
    if (!title) {
      return;
    }

    fetch(`${Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        const line = cart.items.find(
          (item) => item.properties && item.properties._mr === '1' && item.title.indexOf(title) === 0
        );
        if (line) {
          return fetch(`${Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        }
      })
      .then(() => {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
  };

  // Reflect Monk's current picker state into our band: show only while a gift widget is active
  const monkUpdatePicker = () => {
    monkUpdateQueued = false;

    const picker = monkCartDrawer.querySelector('.cart-drawer__gwp');
    if (!picker) {
      return;
    }

    const anchors = monkAnchors();
    const anchor = monkActiveAnchor();
    const hasWidget = anchors.some((el) => el.querySelector('.mr-embedded-widget'));
    const active = !!anchor;

    picker.classList.toggle('cart-drawer__gwp--empty', !hasWidget);
    picker.classList.toggle('cart-drawer__gwp--loading', hasWidget && !active);

    // Flush the footer's top padding while the band shows
    monkCartDrawer.classList.toggle('cart-drawer--gwp-active', active);

    if (active) {
      monkPickerSeen = true;

      // If both the embed-ID anchor and the default rendered cards, keep the preferred one and
      // hide the duplicate (only ever hiding an anchor that already has cards).
      anchors.forEach((el) => {
        el.hidden = el !== anchor && !!el.querySelector('.mr-product-item');
      });

      // Flag added cards as − and make Monk's disabled "Added" button removable on click.
      // Monk marks added cards by label only (hashed classes aren't stable), so key off the text.
      anchor.querySelectorAll('.mr-actions__atc-button').forEach((button) => {
        const title = monkCardTitle(button.closest('.mr-product-item'));
        button.classList.toggle('gwp-loading', !!title && monkLoadingTitles.has(title));

        const added = button.textContent.trim() === 'Added';
        button.classList.toggle('gwp-added', added);

        if (added) {
          button.disabled = false;

          if (!button.dataset.gwpRemove) {
            button.dataset.gwpRemove = '1';
            button.addEventListener('click', (event) => {
              if (button.textContent.trim() !== 'Added') {
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              monkRemoveGift(button.closest('.mr-product-item'));
            });
          }
        }
      });

      monkSyncPicker(anchor);

      // Dots: attach a scroll listener once per list, then sync the active dot.
      const list = anchor.querySelector('.react-multi-carousel-list');
      if (list && !list.dataset.gwpScroll) {
        list.dataset.gwpScroll = '1';
        list.addEventListener('scroll', monkUpdateDots, { passive: true });
      }
      monkUpdateDots();
    }
  };

  // Build one dot per card and mark the one nearest the scroll position as active. Cards are
  // dynamic (Monk re-renders), so rebuild only when the count changes.
  const monkUpdateDots = () => {
    const list = monkActiveList();
    const dots = monkCartDrawer.querySelector('[data-gwp-dots]');
    const carousel = monkCartDrawer.querySelector('.cart-drawer__gwp-carousel');
    if (!list || !dots || !carousel) {
      return;
    }

    const items = list.querySelectorAll('.react-multi-carousel-item');
    const count = items.length;
    dots.toggleAttribute('hidden', count <= 1);
    if (count <= 1) {
      dots.replaceChildren();
      return;
    }

    if (dots.childElementCount !== count) {
      const next = document.createDocumentFragment();
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cart-drawer__gwp-dot';
        dot.dataset.gwpDot = String(i);
        dot.setAttribute('aria-label', `Gift ${i + 1}`);
        next.appendChild(dot);
      }
      dots.replaceChildren(next);
    }

    // Align the dot row with the card's left edge (Monk insets the carousel from the band edge).
    const firstCard = list.querySelector('.mr-product-item__body-wrapper') || items[0];
    const inset = firstCard.getBoundingClientRect().left + list.scrollLeft - carousel.getBoundingClientRect().left;
    dots.style.marginInlineStart = `${Math.max(0, inset)}px`;

    // Active dot = card nearest the scroll position; force the last at the scroll end (the carousel
    // often can't scroll a full step to the final card, so rounding alone never selects it).
    const step = items[1] ? items[1].offsetLeft - items[0].offsetLeft : items[0].offsetWidth;
    const atEnd = list.scrollLeft >= list.scrollWidth - list.clientWidth - 2;
    const activeIndex = atEnd
      ? count - 1
      : step > 0
        ? Math.min(count - 1, Math.max(0, Math.round(list.scrollLeft / step)))
        : 0;
    [...dots.children].forEach((dot, i) => {
      dot.classList.toggle('cart-drawer__gwp-dot--active', i === activeIndex);
    });
  };

  // Coalesce Monk's mutations (render, re-inject) into a single update per frame.
  new MutationObserver(() => {
    if (monkUpdateQueued) {
      return;
    }
    monkUpdateQueued = true;
    requestAnimationFrame(monkUpdatePicker);
  }).observe(monkCartDrawer, { childList: true, subtree: true });

  // Dot nav: scroll the carousel to the chosen card.
  monkCartDrawer.addEventListener('click', (event) => {
    const dot = event.target.closest('[data-gwp-dot]');
    if (!dot) {
      return;
    }
    const list = monkActiveList();
    const items = list && list.querySelectorAll('.react-multi-carousel-item');
    const index = Number(dot.dataset.gwpDot);
    if (items && items[index] && items[1]) {
      const step = items[1].offsetLeft - items[0].offsetLeft;
      list.scrollTo({ left: index * step, behavior: 'smooth' });
    }
  });

  // Loading aesthetic: when a gift's add/remove button is clicked, swap its +/− for the dot
  // loader. The drawer re-mount clears the loader. the timeout is a safety net if no re-mount follows.
  monkCartDrawer.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.mr-actions__atc-button');
      if (!button) {
        return;
      }
      button.classList.add('gwp-loading');
      const title = monkCardTitle(button.closest('.mr-product-item'));
      if (title) {
        monkLoadingTitles.add(title);
        setTimeout(() => {
          monkLoadingTitles.delete(title);
          monkUpdatePicker();
        }, 8000);
      }
    },
    true
  );

  document.addEventListener('cart-drawer:refreshed', () => {
    if (monkPickerSeen) {
      // The add/remove has settled — the rebuilt cards show the final +/− state.
      monkLoadingTitles.clear();
      monkRefreshDrawer();
    }
  });
}

/*
* Ingredients index: the A–Z bar navigates the paginated glossary and rows open a detail panel.
* Letter map and pagination URLs come from data attributes on the element.
*/

const INGREDIENT_ANCHOR = 'ingredient-';

class IngredientsIndex extends HTMLElement {
  constructor() {
    super();
    this.activeRow = null;
    this.alphaObserver = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.mql = window.matchMedia(`(min-width: ${this.dataset.desktopMin || 1000}px)`);
    this.hero = this.querySelector('[data-ix-hero]');
    this.stage = this.querySelector('[data-ix-stage]');
    this.stageContent = this.querySelector('[data-ix-stage-content]');
    this.alphaNav = this.querySelector('[data-ix-alpha-nav]');
    this.alphaLinks = Array.from(this.alphaNav.querySelectorAll('[data-ix-alpha]'));

    this.parsePaging();

    this.querySelector('[data-ix-close]')?.addEventListener('click', () => this.reset(true), { signal: this.signal });
    this.mql.addEventListener('change', () => { this.reset(); this.setupAlphaSpy(); }, { signal: this.signal });
    this.alphaNav.addEventListener('click', (event) => this.onAlphaClick(event), { signal: this.signal });

    this.addEventListener('click', (event) => {
      const row = event.target.closest('[data-ix-trigger]');
      if (row && this.contains(row)) this.onRowClick(row);
    }, { signal: this.signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.activeRow) this.reset(true);
    }, { signal: this.signal });

    this.seedMap();
    this.wireAlpha();
    this.markCurrentLetters();
    this.setupAlphaSpy();
    this.handleHash();
    this.loadAllEntries();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.alphaObserver?.disconnect();
  }

  isDesktop() {
    return this.mql.matches;
  }

  // Section id, page count, and the namespaced page param — read once from a sample paginate URL
  // (the param is namespaced per section, e.g. page_407257a6, so we can't hardcode it).
  parsePaging() {
    this.sectionId = this.dataset.sectionId;
    this.currentPage = parseInt(this.dataset.currentPage || '1', 10);
    this.totalPages = parseInt(this.dataset.pages || '1', 10);
    this.pageParam = 'page';
    const sample = this.dataset.sampleUrl;
    if (!sample) return;
    try {
      new URL(sample, location.origin).searchParams.forEach((value, key) => {
        if (key === 'page' || key.indexOf('page_') === 0) this.pageParam = key;
      });
    } catch (error) {}
  }

  // Section Rendering API URL for a page
  // returns only this section's markup, not the full page.
  pageUrl(page) {
    const url = new URL(location.pathname, location.origin);
    url.searchParams.set(this.pageParam, page);
    if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
    return `${url.pathname}${url.search}`;
  }

  // Map this page's letters before the other pages are fetched, so the alpha bar works immediately.
  seedMap() {
    this.map = {};
    this.querySelectorAll('[data-ix-group]').forEach((group) => {
      this.map[group.dataset.ixGroup] = this.currentPage;
    });
  }

  // Fetch every other page through the Section Rendering API, collect every entry, then sort by name
  // and rebuild the letter groups client-side so the A–Z order is correct regardless of how the
  // metaobject entries are ordered in the admin. The index becomes a single in-page list.
  async loadAllEntries() {
    const list = this.querySelector('[data-ix-list]');
    if (!list) return;

    let allLoaded = true;
    const items = Array.from(list.querySelectorAll('.ingredients-index__item'));

    if (this.totalPages > 1) {
      const docs = new Map();
      const fetches = [];
      for (let page = 1; page <= this.totalPages; page += 1) {
        if (page === this.currentPage) continue;
        fetches.push(
          fetch(this.pageUrl(page), { signal: this.signal })
            .then((response) => response.text())
            .then((html) => docs.set(page, new DOMParser().parseFromString(html, 'text/html')))
            .catch(() => {})
        );
      }
      await Promise.all(fetches);

      for (let page = 1; page <= this.totalPages; page += 1) {
        if (page === this.currentPage) continue;
        const doc = docs.get(page);
        if (!doc) { allLoaded = false; continue; }
        doc.querySelectorAll('.ingredients-index__item').forEach((item) => items.push(document.importNode(item, true)));
      }
    }

    items.sort((a, b) => this.nameOf(a).localeCompare(this.nameOf(b), undefined, { sensitivity: 'base', numeric: true }));

    const fragment = document.createDocumentFragment();
    let group = null;
    let groupLetter = null;
    items.forEach((item) => {
      const letter = item.dataset.letter;
      if (letter !== groupLetter) {
        group = document.createElement('div');
        group.className = 'ingredients-index__group';
        group.id = `${INGREDIENT_ANCHOR}${letter}`;
        group.dataset.ixGroup = letter;
        fragment.appendChild(group);
        groupLetter = letter;
      }
      group.appendChild(item);
    });

    list.replaceChildren(fragment);

    this.map = {};
    this.querySelectorAll('[data-ix-group]').forEach((entry) => { this.map[entry.dataset.ixGroup] = this.currentPage; });

    if (allLoaded) {
      const pagination = this.querySelector('.ingredients-index__pagination');
      if (pagination) pagination.hidden = true;
    }

    this.wireAlpha();
    this.markCurrentLetters();
    this.setupAlphaSpy();
    this.handleHash();
  }

  nameOf(item) {
    return (item.querySelector('.ingredients-index__name')?.textContent || '').trim();
  }

  // href is server-rendered for every letter; just flag which ones lack entries.
  wireAlpha() {
    this.alphaLinks.forEach((link) => {
      const letter = link.dataset.ixAlpha;
      const hasEntries = this.map[letter] != null;
      link.classList.toggle('is-disabled', !hasEntries);
      link.setAttribute('aria-disabled', String(!hasEntries));
    });
  }

  onAlphaClick(event) {
    const link = event.target.closest('[data-ix-alpha]');
    if (!link || !this.alphaNav.contains(link)) return;
    event.preventDefault();
    if (link.classList.contains('is-disabled')) return;
    this.scrollToLetter(link.dataset.ixAlpha);
  }

  setActiveLetter(letter) {
    this.alphaLinks.forEach((link) => link.classList.toggle('is-active', link.dataset.ixAlpha === letter));
  }

  // On desktop the list has its own scroll container; scrollIntoView() also bubbles the scroll
  // up to the window (to fully reveal that container), which nudges the page and hides the alpha
  // nav behind the site header. Scroll the list container directly instead, so the window never moves.
  scrollToLetter(letter) {
    const group = document.getElementById(`${INGREDIENT_ANCHOR}${letter}`);
    if (!group) return;

    const list = this.isDesktop() ? this.querySelector('[data-ix-list]') : null;
    if (list) {
      const top = group.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
      list.scrollTo({ top, behavior: 'smooth' });
    } else {
      group.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    this.setActiveLetter(letter);
  }

  handleHash() {
    const hash = (location.hash || '').replace('#', '');
    if (hash.indexOf(INGREDIENT_ANCHOR) !== 0) return;
    requestAnimationFrame(() => this.scrollToLetter(hash.slice(INGREDIENT_ANCHOR.length)));
  }

  markCurrentLetters() {
    const first = this.querySelector('[data-ix-group]');
    this.setActiveLetter(first && first.dataset.ixGroup);
  }

  // Scroll-spy: highlight the letter whose group sits near the top of the scrolling list.
  setupAlphaSpy() {
    if (!('IntersectionObserver' in window)) return;
    this.alphaObserver?.disconnect();
    const root = this.isDesktop() ? this.querySelector('[data-ix-list]') : null;
    this.alphaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActiveLetter(entry.target.dataset.ixGroup);
        });
      },
      { root, rootMargin: '-20% 0px -75% 0px' }
    );
    this.querySelectorAll('[data-ix-group]').forEach((group) => this.alphaObserver.observe(group));
  }

  detailFor(row) {
    return row.closest('.ingredients-index__item').querySelector('[data-ix-detail]');
  }

  onRowClick(row) {
    if (row === this.activeRow) { this.reset(); return; }
    this.reset();
    this.activeRow = row;
    row.setAttribute('aria-expanded', 'true');
    const detail = this.detailFor(row);
    detail.hidden = false;
    if (this.isDesktop()) {
      this.stageContent.appendChild(detail);
      this.stage.hidden = false;
      this.hero.hidden = true;
      this.stage.scrollTop = 0;
    } else {
      detail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  reset(returnFocus) {
    const row = this.activeRow;
    if (row) {
      const detail = this.stageContent.querySelector('[data-ix-detail]') || this.detailFor(row);
      detail.hidden = true;
      const home = row.closest('.ingredients-index__item');
      if (detail.parentElement !== home) home.appendChild(detail);
      row.setAttribute('aria-expanded', 'false');
      this.activeRow = null;
    }
    this.stage.hidden = true;
    this.hero.hidden = false;
    if (returnFocus && row) row.focus();
  }
}

if (!window.customElements.get('ingredients-index')) {
  window.customElements.define('ingredients-index', IngredientsIndex);
}

/*
* Respect prefers-reduced-motion for autoplaying video-media: block any play() that didn't
* originate from the pause/play control below, so background/hero videos never self-start motion
* for users who've asked for reduced motion. BaseMedia's own inView-triggered autoplay and the
* slideshow carousel's play-on-select both go through this same method with nothing else to
* distinguish "automatic" from "user pressed play" — video-media is defined in theme.js
* (vendor/sync-managed), so this patches its prototype from here rather than editing that file.
*/
if (window.customElements.get('video-media')) {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const videoMediaProto = window.customElements.get('video-media').prototype;
  const originalPlay = videoMediaProto.play;

  videoMediaProto.play = function (...args) {
    if (reducedMotionQuery.matches && !this.dataset.pauseControlStarted) {
      return undefined;
    }
    return originalPlay.apply(this, args);
  };
}

/*
* Play/pause button for autoplaying video-media elements (see snippets/video-pause-control.liquid).
* Tracks the target(s)' `playing` attribute via MutationObserver so the icon stays in sync
* whether playback starts/stops from a click, autoplay-on-scroll, or a carousel selecting the slide.
*/
class VideoPauseControl extends HTMLElement {
  connectedCallback() {
    this.targets = (this.getAttribute('aria-controls') || '')
      .split(/\s+/)
      .filter(Boolean)
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!this.targets.length) return;

    this.button = this.querySelector('button');
    this.pauseLabel = this.dataset.pauseLabel;
    this.playLabel = this.dataset.playLabel;

    this.button.addEventListener('click', (event) => {
      // The slideshow (and other callers) may wrap the whole slide in an <a>; stop the click
      // from bubbling into it, otherwise pressing pause/play also follows the slide link.
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    });

    this.observer = new MutationObserver(() => this.sync());
    this.targets.forEach((target) => this.observer.observe(target, { attributes: true, attributeFilter: ['playing'] }));

    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  toggle() {
    const playing = this.targets.some((target) => target.playing);
    this.targets.forEach((target) => {
      if (playing) {
        target.pause();
      } else {
        // Flags this as user-initiated so the reduced-motion gate above lets it through.
        target.dataset.pauseControlStarted = 'true';
        target.play();
      }
    });
  }

  sync() {
    const playing = this.targets.some((target) => target.playing);
    this.toggleAttribute('playing', playing);
    this.button.setAttribute('aria-label', playing ? this.pauseLabel : this.playLabel);
  }
}

if (!window.customElements.get('video-pause-control')) {
  window.customElements.define('video-pause-control', VideoPauseControl);
}

/*
* Orbe Geolocation compatibility: our localization-selector submits country_code/locale_code
* via Shopify's native `localization` form, but Orbe tracks the user's choice in its own
* cookies. Without this, picking a location in our selector after Orbe's popup left its cookies
* set to something else caused Orbe to redirect the visitor back to its own prior selection.
* Cookie contract: https://help.orbe.app/developer-reference/integrate-your-selector
*/

document.addEventListener('submit', (event) => {
  const form = event.target;

  if (!form.id?.startsWith('localization-form-')) {
    return;
  }

  const submitter = event.submitter;
  const value = submitter?.value;

  if (!value) {
    return;
  }

  const shopDomain = (typeof orbito !== 'undefined' && orbito.getCountryDomain)
    ? orbito.getCountryDomain() || window.location.hostname.replace(/^www\./, '')
    : window.location.hostname.replace(/^www\./, '');

  const cookieAttrs = `domain=${shopDomain}; path=/; max-age=${60 * 60 * 24 * 60};`;

  if (submitter.name === 'country_code') {
    document.cookie = `mdApp_countryCodeDomain=${value}; ${cookieAttrs}`;

    const returnToInput = form.querySelector('input[name="return_to"]');

    if (returnToInput) {
      returnToInput.value = returnToInput.value.match(/(\?|&)country=[^&]*/)
        ? returnToInput.value.replace(/(\?|&)country=[^&]*/, `$1country=${value}`)
        : returnToInput.value + (returnToInput.value.includes('?') ? `&country=${value}` : `?country=${value}`);
    }
  } else if (submitter.name === 'locale_code') {
    document.cookie = `mdApp_showRecommendationLang=${value}; ${cookieAttrs}`;
  }
});

/*
* Mobile menu drawer: collapse any open nav accordions once the drawer has fully closed,
* so it reopens in its initial state. dialog:after-hide doesn't bubble, hence the capture listener.
*/
document.addEventListener('dialog:after-hide', (event) => {
  if (event.target.id !== 'menu-drawer') {
    return;
  }

  event.target.querySelectorAll('.menu-drawer__accordion.is-open').forEach((accordion) => {
    accordion.classList.remove('is-open');
    accordion.querySelector('details').open = false;
  });
}, { capture: true });

/*
* Product card row alignment: card info is bottom-anchored over the image, so a taller info (2-line
* title or descriptor) pushes its type/title above the rest of the row. Give every .product-card__info
* in a visual row the height of that row's tallest one; CSS (custom.css) then justifies type/title
* to the top and descriptor/price to the bottom. Rows are grouped by card offsetTop, so this handles
* any column count / breakpoint / carousel.
*/
const equalizeProductCardInfo = (list) => {
  const cards = [...list.querySelectorAll('.product-card')];
  const infos = cards.map((card) => card.querySelector('.product-card__info'));

  // Reset first, then batch reads before writes to avoid layout thrash.
  infos.forEach((info) => info && (info.style.minBlockSize = ''));

  const rows = new Map();

  cards.forEach((card, index) => {
    const info = infos[index];

    if (!info || card.offsetParent === null) {
      return;
    }

    const top = Math.round(card.offsetTop);
    const row = rows.get(top) || { infos: [], max: 0 };

    row.infos.push(info);
    row.max = Math.max(row.max, info.getBoundingClientRect().height);
    rows.set(top, row);
  });

  rows.forEach(({ infos: rowInfos, max }) => {
    if (rowInfos.length > 1) {
      rowInfos.forEach((info) => (info.style.minBlockSize = `${max}px`));
    }
  });
};

const observedProductLists = new WeakMap();

const productListResizeObserver = new ResizeObserver((entries) => {
  entries.forEach(({ target, contentRect }) => {
    // Only width changes re-flow titles; ignore height changes (incl. our own writes).
    if (observedProductLists.get(target) !== contentRect.width) {
      observedProductLists.set(target, contentRect.width);
      equalizeProductCardInfo(target);
    }
  });
});

const observeProductLists = () => {
  document.querySelectorAll('.product-list').forEach((list) => {
    if (observedProductLists.has(list)) {
      equalizeProductCardInfo(list); // cards may have been added (pagination / load more)
    } else {
      observedProductLists.set(list, null);
      productListResizeObserver.observe(list);
    }
  });
};

let productListScanQueued = false;

// Catch section re-renders (facets, sorting, pagination) that swap in new lists or cards.
new MutationObserver((mutations) => {
  if (productListScanQueued) {
    return;
  }

  const touchesProductCards = mutations.some(({ addedNodes }) => [...addedNodes].some((node) =>
    node.nodeType === Node.ELEMENT_NODE && (node.matches('.product-card, .product-list') || node.querySelector('.product-card'))
  ));

  if (touchesProductCards) {
    productListScanQueued = true;
    requestAnimationFrame(() => {
      productListScanQueued = false;
      observeProductLists();
    });
  }
}).observe(document.body, { childList: true, subtree: true });

observeProductLists();
document.fonts?.ready.then(() => document.querySelectorAll('.product-list').forEach(equalizeProductCardInfo));
